import { Component, inject, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { userApi } from '@vectronic/openApi-customer';
import { SnackbarService } from '@vectronic/util';
import { take } from 'rxjs';
import { CardComponent } from '../card/card.component';
import { ImageInputComponent } from './image-input.component';

@Component({
  selector: 'vectronic-report-issue',
  imports: [
    CardComponent,
    MatDialogModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    ImageInputComponent,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './report-issue.component.html',
  styleUrl: './report-issue.component.scss',
})
export class ReportIssueComponent {
  reportIssueService = inject(userApi.ReportIssues);
  snackbarService = inject(SnackbarService);
  dialogRef = inject(MatDialogRef<ReportIssueComponent>);
  private fb = inject(FormBuilder);
  data: { applicationDescription: string } = inject(MAT_DIALOG_DATA);

  reportIssueForm: FormGroup;
  selectedImages: File[] = [];
  private local = window.desktop ?? window.adminDesktop ?? window.mobile;
  isDesktop = Boolean(window.desktop || window.adminDesktop);
  isMobile = Boolean(window.mobile);
  isOnline = true;
  offlineTooltipMessage = 'Need to be online to send a report.';

  submitting = false;

  @ViewChild(ImageInputComponent) imageInput!: ImageInputComponent;
  constructor() {
    if (this.local) {
      this.updateStatus();
      if ('onSwitchToOffline' in this.local.app) {
        this.local.app.onSwitchToOffline(() => this.updateStatus());
        this.local.app.onSwitchToOnline(() => this.updateStatus());
      }
    }
    this.reportIssueForm = this.fb.group({
      subject: ['', Validators.required],
      comment: ['', [Validators.required, Validators.minLength(8)]],
      includeLogs: [true],
      images: [[]],
    });
  }

  private updateStatus() {
    this.local?.app
      .netIsOnline()
      .pipe(take(1))
      .subscribe((isOnline) => {
        this.isOnline = isOnline;
        if (isOnline) this.reportIssueForm.enable();
        else this.reportIssueForm.disable();
      });
  }

  onImagesSelected(files: File[]) {
    this.selectedImages = files;
    this.reportIssueForm.patchValue({ images: files });
  }

  onSubmit() {
    if (this.reportIssueForm.invalid || this.submitting) return;
    this.submitting = true;
    this.dialogRef.disableClose = true;
    const subject = this.reportIssueForm.get('subject')?.value;
    const comment = this.reportIssueForm.get('comment')?.value;
    const files = this.selectedImages;

    if (this.local) {
      const includeLogs = this.reportIssueForm.get('includeLogs')?.value;
      Promise.all(
        files.map((file) =>
          file.arrayBuffer().then((content) => ({ name: file.name, content, type: file.type })),
        ),
      )
        .then((desktopFiles) => {
          this.local.app.reportIssue(subject, comment, desktopFiles, includeLogs).subscribe({
            next: () => this.dialogRef.close(true),
            error: () => {
              this.submitting = false;
              this.dialogRef.disableClose = false;
            },
          });
        })
        .catch(() => {
          this.submitting = false;
          this.dialogRef.disableClose = false;
          this.snackbarService.error('Failed to load attachment files.');
        });
    } else {
      this.reportIssueService
        .reportIssue(comment, this.data.applicationDescription, subject, files)
        .subscribe({
          next: () => this.dialogRef.close(true),
          error: () => {
            this.submitting = false;
            this.dialogRef.disableClose = false;
          },
        });
    }
  }
}
